import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Heart, Loader2 } from 'lucide-react';
import { useFullscreen } from '@/contexts/FullscreenContext';
import { useAuth } from '@/contexts/AuthContext';
import { API_BASE_URL } from '@/services/httpClient';

/**
 * BreathingFree - Écran de respiration apaisante
 * 
 * - Gate Premium/Free basé sur useAuth (source unique de vérité)
 * - Loading state pour éviter le "flash free"
 * - i18n complet FR/EN
 */
const BreathingFree = ({ t }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSilentMode = searchParams.get('silent') === 'true';
  const { setIsFullscreen } = useFullscreen();
  const { user, loading: authLoading } = useAuth();
  
  const [isActive, setIsActive] = useState(false);
  const [phase, setPhase] = useState('ready'); // ready, inhale, hold, exhale, complete
  const [timeRemaining, setTimeRemaining] = useState(60); // 1 minute fixe
  const [cycleCount, setCycleCount] = useState(0);
  const [heartScale, setHeartScale] = useState(0.8);
  const [heartOpacity, setHeartOpacity] = useState(0.6);
  const timerRef = useRef(null);
  const phaseTimerRef = useRef(null);
  const audioContextRef = useRef(null);
  const musicSourceRef = useRef(null);
  const musicGainRef = useRef(null);

  // Source unique de vérité pour le statut Premium (admins inclus)
  const isPremium = user?.isPremium === true || user?.subscription === 'premium' || user?.role === 'admin';
  
  // Langue actuelle
  const lang = localStorage.getItem('hearen_lang') || 'fr';

  // Textes i18n
  const texts = {
    title: lang === 'en' 
      ? (isSilentMode ? 'Simple Breathing' : 'Calming Breathing')
      : (isSilentMode ? 'Respiration Simple' : 'Respiration Apaisante'),
    pattern: lang === 'en' 
      ? `4-2-6 • 1 minute${isSilentMode ? ' • Silent' : ''}`
      : `4-2-6 • 1 minute${isSilentMode ? ' • Silencieux' : ''}`,
    premiumNotice: lang === 'en'
      ? '🔒 Premium features available: advanced exercises, custom durations, Hera guidance.'
      : '🔒 Fonctions Premium disponibles : exercices avancés, durées personnalisées, guidage Hera.',
    ready: lang === 'en' ? 'Ready to begin?' : 'Prêt à commencer ?',
    inhale: lang === 'en' ? 'Breathe in deeply...' : 'Inspirez profondément...',
    hold: lang === 'en' ? 'Hold your breath...' : 'Retenez votre souffle...',
    exhale: lang === 'en' ? 'Exhale slowly...' : 'Expirez lentement...',
    complete: lang === 'en' ? 'Well done! Exercise complete 🎉' : 'Bravo ! Exercice terminé 🎉',
    cycle: lang === 'en' ? 'Cycle' : 'Cycle',
    premiumTip: lang === 'en' 
      ? 'Tip: Hera voice guidance is available in Premium.'
      : 'Astuce : Le guidage vocal Hera est disponible dans Premium.',
    discoverPremium: lang === 'en' ? 'Discover Premium' : 'Découvrir Premium',
    start: lang === 'en' ? 'Start' : 'Commencer',
    stop: lang === 'en' ? 'Stop' : 'Arrêter',
    back: lang === 'en' ? 'Back' : 'Retour',
    restart: lang === 'en' ? 'Restart' : 'Recommencer',
  };

  const TOTAL_CYCLES = 5; // 5 cycles × 12 secondes = 60 secondes (1 minute)
  const INHALE_DURATION = 4000; // 4 secondes
  const HOLD_DURATION = 2000; // 2 secondes
  const EXHALE_DURATION = 6000; // 6 secondes

  // Activer le mode plein écran
  useEffect(() => {
    setIsFullscreen(true);
    return () => setIsFullscreen(false);
  }, [setIsFullscreen]);

  // Animation du cœur basée sur la phase
  useEffect(() => {
    if (!isActive) {
      setHeartScale(0.8);
      setHeartOpacity(0.6);
      return;
    }

    if (phase === 'inhale') {
      setHeartScale(1.8);
      setHeartOpacity(1);
    } else if (phase === 'hold') {
      setHeartScale(1.8);
      setHeartOpacity(1);
    } else if (phase === 'exhale') {
      setHeartScale(0.9);
      setHeartOpacity(0.5);
    }
  }, [phase, isActive]);

  // Charger et jouer la musique d'ambiance
  const playBackgroundMusic = async () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      const audioContext = audioContextRef.current;
      
      musicGainRef.current = audioContext.createGain();
      musicGainRef.current.gain.value = 0;
      musicGainRef.current.connect(audioContext.destination);

      // Safe fallback for API_BASE_URL
      const baseUrl = API_BASE_URL || 'https://hearen-backend.onrender.com';
      
      // Use centralized API_BASE_URL for iOS Capacitor compatibility
      const soundsResponse = await fetch(`${baseUrl}/api/library-sounds/respiration`);
      const soundsData = await soundsResponse.json();

      let musicPath = '/sounds/respiration/sweet_dreams.mp3';
      if (soundsData.sounds && soundsData.sounds.length > 0) {
        const randomSound = soundsData.sounds[Math.floor(Math.random() * soundsData.sounds.length)];
        musicPath = randomSound.path;
      }
      
      const originalPath = musicPath;
      // Normalize relative URLs to absolute with safe fallback (required for iOS Capacitor)
      if (musicPath && musicPath.startsWith('/')) {
        musicPath = `${baseUrl}${musicPath}`;
        console.log(`🔄 [Breathing] Normalized music URL to: ${musicPath}`);
      }
      
      // GUARD: Only allow http(s) URLs
      if (!musicPath || !/^https?:\/\//i.test(musicPath)) {
        console.error(`❌ [Breathing] BLOCKED: base=${baseUrl}, original=${originalPath}, final=${musicPath}`);
        return;
      }

      const response = await fetch(musicPath);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      musicSourceRef.current = audioContext.createBufferSource();
      musicSourceRef.current.buffer = audioBuffer;
      musicSourceRef.current.loop = true;
      musicSourceRef.current.connect(musicGainRef.current);
      musicSourceRef.current.start(0);

      const currentTime = audioContext.currentTime;
      musicGainRef.current.gain.setValueAtTime(0, currentTime);
      musicGainRef.current.gain.linearRampToValueAtTime(0.15, currentTime + 2);

    } catch (error) {
      console.error('Error playing background music:', error);
    }
  };

  // Arrêter la musique
  const stopBackgroundMusic = () => {
    if (musicGainRef.current && audioContextRef.current) {
      const currentTime = audioContextRef.current.currentTime;
      musicGainRef.current.gain.setValueAtTime(musicGainRef.current.gain.value, currentTime);
      musicGainRef.current.gain.linearRampToValueAtTime(0, currentTime + 1);

      setTimeout(() => {
        if (musicSourceRef.current) {
          try {
            musicSourceRef.current.stop();
          } catch (e) {
            // Already stopped
          }
          musicSourceRef.current = null;
        }
      }, 1000);
    }
  };

  // Démarrer l'exercice
  const startExercise = () => {
    setIsActive(true);
    setTimeRemaining(60);
    setCycleCount(0);
    startBreathingCycle();
    
    if (!isSilentMode) {
      playBackgroundMusic();
    }

    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          completeExercise();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Cycle de respiration
  const startBreathingCycle = () => {
    setCycleCount(prev => prev + 1);
    
    setPhase('inhale');
    phaseTimerRef.current = setTimeout(() => {
      setPhase('hold');
      phaseTimerRef.current = setTimeout(() => {
        setPhase('exhale');
        phaseTimerRef.current = setTimeout(() => {
          if (cycleCount < TOTAL_CYCLES - 1) {
            startBreathingCycle();
          } else {
            completeExercise();
          }
        }, EXHALE_DURATION);
      }, HOLD_DURATION);
    }, INHALE_DURATION);
  };

  // Compléter l'exercice
  const completeExercise = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (phaseTimerRef.current) clearTimeout(phaseTimerRef.current);
    setPhase('complete');
    setIsActive(false);
    stopBackgroundMusic();
  };

  // Arrêter l'exercice manuellement
  const stopExercise = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (phaseTimerRef.current) clearTimeout(phaseTimerRef.current);
    setIsActive(false);
    setPhase('ready');
    stopBackgroundMusic();
  };

  // Nettoyage
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (phaseTimerRef.current) clearTimeout(phaseTimerRef.current);
      stopBackgroundMusic();
    };
  }, []);

  // Messages de guidance i18n
  const getPhaseMessage = () => {
    if (phase === 'ready') return texts.ready;
    if (phase === 'inhale') return texts.inhale;
    if (phase === 'hold') return texts.hold;
    if (phase === 'exhale') return texts.exhale;
    if (phase === 'complete') return texts.complete;
    return '';
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Loading state - prevent "flash free"
  if (authLoading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{
          background: 'radial-gradient(circle at 50% 40%, #10b981 0%, #059669 40%, #047857 100%)',
        }}
      >
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen relative overflow-hidden transition-colors duration-1000"
      style={{
        background: isActive && phase === 'complete'
          ? 'linear-gradient(135deg, #059669 0%, #047857 50%, #065f46 100%)'
          : 'radial-gradient(circle at 50% 40%, #10b981 0%, #059669 40%, #047857 100%)',
      }}
    >
      {/* Halo doux premium derrière le cœur */}
      <div
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none transition-all duration-1000"
        style={{
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(147, 197, 253, 0.15) 0%, rgba(99, 102, 241, 0.1) 40%, transparent 70%)',
          filter: 'blur(80px)',
          opacity: isActive ? 0.6 : 0.3,
        }}
      />

      {/* Bouton retour */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-6 left-6 z-50 w-12 h-12 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center backdrop-blur-md transition-all"
      >
        <ArrowLeft className="text-white" size={24} />
      </button>

      {/* Contenu principal */}
      <div className="flex flex-col items-center justify-center min-h-screen px-6 py-20">
        {/* Titre */}
        <h1 className="text-3xl font-bold text-white mb-2 text-center">
          {texts.title}
        </h1>
        <p className="text-white/80 mb-3 text-center">
          {texts.pattern}
        </p>
        
        {/* Bandeau upsell - UNIQUEMENT pour utilisateurs FREE */}
        {!isPremium && (
          <p className="text-white/70 text-sm text-center mb-8 max-w-md">
            {texts.premiumNotice}
          </p>
        )}
        
        {/* Spacer si Premium (pour garder le layout cohérent) */}
        {isPremium && <div className="mb-8" />}

        {/* Animation du cœur */}
        <div className="relative mb-8" style={{ width: '300px', height: '300px' }}>
          {/* Halo externe */}
          <div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full transition-all duration-[1500ms] ease-in-out"
            style={{
              width: `${heartScale * 200}px`,
              height: `${heartScale * 200}px`,
              background: `radial-gradient(circle, rgba(16, 185, 129, ${heartOpacity * 0.3}) 0%, rgba(5, 150, 105, ${heartOpacity * 0.2}) 50%, transparent 70%)`,
              filter: 'blur(40px)',
            }}
          />

          {/* Cœur principal */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <Heart
              size={150}
              className="text-white transition-all duration-[1500ms] ease-in-out"
              fill="rgba(255, 255, 255, 0.9)"
              style={{
                transform: `scale(${heartScale})`,
                opacity: heartOpacity,
                filter: `drop-shadow(0 0 ${heartScale * 30}px rgba(16, 185, 129, ${heartOpacity}))`,
              }}
            />
          </div>
        </div>

        {/* Message de guidance */}
        <div className="text-center mb-6">
          <p className="text-2xl font-semibold text-white mb-2">
            {getPhaseMessage()}
          </p>
          {isActive && (
            <>
              <p className="text-white/70 text-lg mb-2">
                {texts.cycle} {cycleCount + 1} / {TOTAL_CYCLES}
              </p>
              {/* Astuce Premium - UNIQUEMENT pour utilisateurs FREE */}
              {!isPremium && (
                <p className="text-white/60 text-xs italic mt-2">
                  {texts.premiumTip}
                </p>
              )}
            </>
          )}
        </div>

        {/* Timer */}
        {isActive && (
          <div className="text-5xl font-bold text-white mb-8">
            {formatTime(timeRemaining)}
          </div>
        )}

        {/* Boutons */}
        <div className="flex flex-col items-center gap-4">
          {!isActive && phase !== 'complete' && (
            <>
              {/* Bouton upsell Premium - UNIQUEMENT pour utilisateurs FREE */}
              {!isPremium && (
                <button
                  onClick={() => navigate('/premium')}
                  className="px-6 py-2 bg-gradient-to-r from-purple-500/20 to-blue-500/20 text-white/90 rounded-full text-sm font-medium hover:from-purple-500/30 hover:to-blue-500/30 backdrop-blur-md transition-all hover:scale-105 active:scale-95 border border-white/10"
                >
                  {texts.discoverPremium}
                </button>
              )}
              
              <button
                onClick={startExercise}
                className="px-8 py-4 bg-white text-emerald-600 rounded-full font-semibold text-lg hover:bg-white/90 transition-all hover:scale-105 active:scale-95 shadow-lg"
              >
                {texts.start}
              </button>
            </>
          )}

          {isActive && (
            <button
              onClick={stopExercise}
              className="px-8 py-4 bg-white/20 text-white rounded-full font-semibold text-lg hover:bg-white/30 backdrop-blur-md transition-all hover:scale-105 active:scale-95"
            >
              {texts.stop}
            </button>
          )}

          {phase === 'complete' && (
            <>
              <button
                onClick={() => navigate(-1)}
                className="px-8 py-4 bg-white text-emerald-600 rounded-full font-semibold text-lg hover:bg-white/90 transition-all hover:scale-105 active:scale-95 shadow-lg"
              >
                {texts.back}
              </button>
              <button
                onClick={() => {
                  setPhase('ready');
                  startExercise();
                }}
                className="px-8 py-4 bg-white/20 text-white rounded-full font-semibold text-lg hover:bg-white/30 backdrop-blur-md transition-all hover:scale-105 active:scale-95"
              >
                {texts.restart}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BreathingFree;
